import type { RefObject } from "react";
import { MessageCircle, QrCode, UserCog } from "lucide-react";
import biancaPhoto from "@/assets/igrejas/pessoas/bianca-ieq-central.webp";
import { useSectionViewGoal } from "@/hooks/useSectionViewGoal";
import { SectionHeading } from "./primitives";
import { CONTAINER, SECTION_Y } from "./ui-tokens";

/**
 * Implantação e adoção — as duas objeções de "dá trabalho trocar?".
 *
 * A primeira é a do comprador (quem migra, quanto custa, quando começa); os
 * três passos respondem. A segunda derruba mais venda de software de igreja do
 * que preço: "eu gostei, mas a minha secretária não vai usar". Ela era uma
 * seção própria (adoption.tsx) logo antes desta; juntas, a pessoa lê as duas
 * respostas no mesmo lugar em vez de rolar duas telas de objeção.
 *
 * O passo 02 é o herdeiro da antiga seção de migração: o que o comprador
 * precisa saber cabe aqui. O detalhe (mapeamento de colunas) segue em
 * /features e na pergunta de migração da FAQ.
 *
 * Cada pilar de adoção aponta para algo que já existe no produto:
 *  - links públicos sem login (rotas fora do AppProviders em routes.tsx:
 *    /escalas/disponibilidade, /escalas/preferencias, /ministerios/chamada,
 *    /membros/:idDaIgreja) e a Central do Membro (cadastro, escalas,
 *    carteirinha — a mesma lista da pergunta 1.9 da FAQ). Indisponíveis
 *    apenas no plano gratuito.
 *  - permissão por tipo de acesso, a mesma descrita em data-trust.tsx.
 *  - suporte humano no WhatsApp.
 * Não acrescente pilar aqui sem que exista tela correspondente.
 */
const steps = [
  {
    title: "Você cria a conta em 1 minuto",
    desc: "Escolha o plano pelo tamanho da igreja. Sem taxa de adesão, sem contrato de fidelidade.",
  },
  {
    title: "Nossa equipe migra a base em até 72h",
    desc: "Você manda a planilha do jeito que ela está, sem renomear coluna nem padronizar data. Começamos por um lote piloto de 50 a 100 pessoas para você conferir; aprovado, trazemos a base inteira, com ministérios, batismo e histórico. Nada disso é cobrado.",
  },
  {
    title: "A igreja opera no próximo culto",
    desc: "Secretaria, liderança e tesouraria começam no sistema pronto, com suporte humano direto no WhatsApp.",
  },
];

const pillars = [
  {
    icon: QrCode,
    title: "Membro, voluntário e líder de célula não criam conta",
    desc: "Eles recebem um link e respondem pelo navegador do celular: a chamada da célula, os dias em que não podem servir, o culto que preferem. O membro atualiza o próprio cadastro, vê as escalas em que está e abre a carteirinha dos filhos para o check-in do infantil, e a igreja chega organizada na mão de quem frequenta.",
  },
  {
    icon: UserCog,
    title: "Cada pessoa entra e vê só a tela dela",
    desc: "A secretária abre no cadastro de pessoas, a tesouraria no financeiro, o líder de ministério na própria equipe. Ninguém aprende mais de vinte módulos para usar o que é seu.",
  },
  {
    icon: MessageCircle,
    title: "A dúvida vai para o WhatsApp, não para um chamado",
    desc: "Na implantação e depois dela, quem mexe no sistema fala com uma pessoa de verdade, no mesmo WhatsApp que já usa o dia inteiro.",
  },
];

/**
 * Depoimento de quem OPERA o sistema — não pastor. É a prova que fecha a
 * objeção de adoção. A Bianca monta escala sem ser da liderança, é de uma
 * equipe pequena e específica (Libras) e é da IEQ Central, a mesma igreja do
 * case: a prova de porte e a de adoção apontam para o mesmo lugar. A citação
 * é a que já está em /stats; se mudar lá, mude aqui. Se um dia sair, deixe
 * `null` em vez de inventar outra.
 */
const operatorQuote: {
  content: string;
  author: string;
  role: string;
  image: string;
} | null = {
  content:
    "Monto as escalas do Ministério de Libras direto na plataforma. Cada intérprete recebe o aviso e confirma sem que eu precise cobrar um por um.",
  author: "Bianca",
  role: "Gestora de Escalas · IEQ Central de Piracicaba",
  image: biancaPhoto,
};

function Adoption() {
  // Mantém o goal da antiga seção, para o funil continuar comparável.
  const ref = useSectionViewGoal("adoption");

  return (
    <div
      ref={ref as RefObject<HTMLDivElement>}
      id="adocao"
      className="mt-20 grid gap-12 md:mt-28 lg:grid-cols-12"
    >
      <div className="lp-reveal lg:col-span-5">
        <h3 className="text-2xl font-semibold tracking-tight text-balance text-foreground md:text-3xl">
          “Eu gostei. Mas será que a minha equipe vai usar?”
        </h3>
        <p className="mt-4 text-base leading-relaxed text-pretty text-muted-foreground">
          Sistema de igreja não morre por falta de recurso. Morre porque só uma
          pessoa aprendeu a mexer, e ela viajou. O Ecclesiato foi montado para
          que quase ninguém precise aprender nada.
        </p>

        {operatorQuote && (
          <figure className="mt-8">
            <blockquote className="text-base leading-relaxed text-foreground">
              “{operatorQuote.content}”
            </blockquote>
            <figcaption className="mt-4 flex items-center gap-3">
              <img
                src={operatorQuote.image}
                alt=""
                className="lp-img size-10 rounded-full object-cover"
                width={40}
                height={40}
                loading="lazy"
              />
              <span className="text-sm">
                <span className="block font-semibold text-foreground">
                  {operatorQuote.author}
                </span>
                <span className="block text-muted-foreground">
                  {operatorQuote.role}
                </span>
              </span>
            </figcaption>
          </figure>
        )}
      </div>

      <ul className="flex flex-col gap-3 lg:col-span-7">
        {pillars.map((pillar) => (
          <li key={pillar.title} className="lp-reveal lp-surface flex gap-4 p-5 md:p-6">
            <span className="grid size-10 shrink-0 place-items-center rounded-full bg-primary/8">
              <pillar.icon
                className="size-4.5 text-primary"
                strokeWidth={1.75}
                aria-hidden="true"
              />
            </span>
            <div>
              <p className="font-semibold text-foreground">{pillar.title}</p>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                {pillar.desc}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function HowItWorks() {
  const ref = useSectionViewGoal("how_it_works");

  return (
    <section
      ref={ref}
      id="como-funciona"
      aria-labelledby="como-funciona-titulo"
      className={`${SECTION_Y} bg-secondary/40`}
    >
      <div className={CONTAINER}>
        <SectionHeading
          id="como-funciona-titulo"
          title="Trocar de sistema dá trabalho."
          muted="Menos aqui."
        >
          Ninguém da sua igreja passa horas cadastrando tudo de novo. A nossa
          equipe faz a transição.
        </SectionHeading>

        {/* O percurso: um fio liga os três passos no desktop, e o número
            mora num círculo sobre ele. No celular o fio fica vertical. */}
        <ol className="relative mt-14 grid gap-10 md:grid-cols-3 md:gap-8">
          <span
            aria-hidden="true"
            className="absolute top-5 bottom-5 left-5 w-px bg-border md:top-5 md:right-[16%] md:bottom-auto md:left-[16%] md:h-px md:w-auto"
          />
          {steps.map((step, i) => (
            <li
              key={step.title}
              className="lp-reveal relative flex gap-5 md:flex-col md:items-center md:text-center"
            >
              <span className="relative grid size-10 shrink-0 place-items-center rounded-full bg-background text-sm font-semibold text-foreground tabular-nums shadow-xs ring-1 ring-border">
                {i + 1}
              </span>
              <div>
                <h3 className="text-lg font-semibold tracking-tight text-foreground">
                  {step.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-pretty text-muted-foreground">
                  {step.desc}
                </p>
              </div>
            </li>
          ))}
        </ol>

        <Adoption />
      </div>
    </section>
  );
}
